/**
 * Bridge between Monaco editor changes and OT operations.
 *
 * Monaco's IModelContentChange provides offset-based replacements:
 *   { rangeOffset, rangeLength, text }
 *
 * We convert these to OT operations and vice versa.
 */

import type { editor } from 'monaco-editor';
import { OTComponents } from '../types/ot';
import { compact, compose } from './otTransform';

/**
 * Convert Monaco model content changes to an OT operation.
 *
 * Monaco can deliver multiple changes in one event (e.g., multi-cursor).
 * Changes are sorted by rangeOffset and processed to build one OT operation.
 * Each change is: retain(offset), delete(rangeLength), insert(text), retain(rest).
 * Multiple changes are composed together.
 */
export function monacoChangesToOTOp(
  changes: readonly editor.IModelContentChange[],
  docLengthBefore: number
): OTComponents {
  if (changes.length === 0) return [];

  // Sort changes by offset ascending — Monaco sometimes delivers in reverse order
  const sorted = [...changes].sort((a, b) => a.rangeOffset - b.rangeOffset);

  // Build a single operation by processing changes from left to right,
  // tracking how previous changes shift subsequent offsets
  let op: OTComponents = [];
  let currentLen = docLengthBefore;

  for (const change of sorted) {
    const singleOp: OTComponents = [];

    if (change.rangeOffset > 0) {
      singleOp.push(change.rangeOffset); // retain before
    }
    if (change.rangeLength > 0) {
      singleOp.push(-change.rangeLength); // delete
    }
    if (change.text.length > 0) {
      singleOp.push(change.text); // insert
    }
    const remaining = currentLen - change.rangeOffset - change.rangeLength;
    if (remaining > 0) {
      singleOp.push(remaining); // retain after
    }

    const compactedSingle = compact(singleOp);
    if (compactedSingle.length === 0) continue;

    if (op.length === 0) {
      op = compactedSingle;
    } else {
      op = compose(op, compactedSingle);
    }

    // Update current length: the document grew/shrunk by this change
    currentLen = currentLen - change.rangeLength + change.text.length;
  }

  return compact(op);
}

/**
 * Convert an OT operation to Monaco edit operations.
 *
 * Returns an array of IIdentifiedSingleEditOperation that can be passed
 * to model.pushEditOperations().
 */
export function otOpToMonacoEdits(
  op: OTComponents,
  model: editor.ITextModel
): editor.IIdentifiedSingleEditOperation[] {
  const edits: editor.IIdentifiedSingleEditOperation[] = [];
  let offset = 0;

  for (const c of op) {
    if (typeof c === 'number' && c > 0) {
      // Retain — advance position
      offset += c;
    } else if (typeof c === 'string') {
      // Insert — add text at current position
      const pos = model.getPositionAt(offset);
      edits.push({
        range: {
          startLineNumber: pos.lineNumber,
          startColumn: pos.column,
          endLineNumber: pos.lineNumber,
          endColumn: pos.column,
        },
        text: c,
        forceMoveMarkers: true,
      });
      // Don't advance offset — inserts don't consume input
    } else if (typeof c === 'number' && c < 0) {
      // Delete — remove characters
      const deleteCount = -c;
      const startPos = model.getPositionAt(offset);
      const endPos = model.getPositionAt(offset + deleteCount);
      edits.push({
        range: {
          startLineNumber: startPos.lineNumber,
          startColumn: startPos.column,
          endLineNumber: endPos.lineNumber,
          endColumn: endPos.column,
        },
        text: '',
        forceMoveMarkers: true,
      });
      offset += deleteCount;
    }
  }

  return edits;
}

/**
 * Transform a Monaco cursor/selection position through an OT operation.
 * Used to update remote cursor positions when operations are applied.
 */
export function transformMonacoPosition(
  position: { lineNumber: number; column: number },
  op: OTComponents,
  model: editor.ITextModel
): { lineNumber: number; column: number } {
  // Convert position to offset, transform, convert back
  const offset = model.getOffsetAt(position);
  let newOffset = offset;
  let pos = 0;

  for (const c of op) {
    if (pos > offset) break;

    if (typeof c === 'number' && c > 0) {
      pos += c;
    } else if (typeof c === 'string') {
      if (pos <= offset) {
        newOffset += c.length;
      }
    } else if (typeof c === 'number' && c < 0) {
      const deleteCount = -c;
      if (pos + deleteCount <= offset) {
        newOffset -= deleteCount;
      } else if (pos < offset) {
        newOffset -= (offset - pos);
      }
      pos += deleteCount;
    }
  }

  // Convert back to line/column (apply op to model first to get new positions)
  // Since the model may not yet be updated, we calculate manually
  return offsetToLineColumn(Math.max(0, newOffset), model.getValue());
}

/** Convert a character offset to line/column in a string */
function offsetToLineColumn(
  offset: number,
  text: string
): { lineNumber: number; column: number } {
  let line = 1;
  let col = 1;
  for (let i = 0; i < Math.min(offset, text.length); i++) {
    if (text[i] === '\n') {
      line++;
      col = 1;
    } else {
      col++;
    }
  }
  return { lineNumber: line, column: col };
}
